import { asString, getInvestorIndustries, getInvestorTypes } from '../../../components/investor-display/helpers';

function normalizeValue(value) {
  return asString(value).trim();
}

function normalizeKey(value) {
  return normalizeValue(value).toLowerCase();
}

function getInvestorDetails(investor) {
  return investor?.details || {};
}

function getInvestorCountry(investor) {
  return normalizeValue(getInvestorDetails(investor)?.country);
}

function getInvestorValue(investor) {
  return Number(investor?.value) || 0;
}

function getInvestorDisplayName(investor) {
  return normalizeValue(getInvestorDetails(investor)?.name || 'Unnamed Investor');
}

function getRecentActivityScore(investor, investorStatsMap = {}) {
  const investorId = String(investor?.investor_id || '');
  const stats = investorStatsMap[investorId];
  const years = Array.isArray(stats?.years) ? stats.years : [];

  if (!years.length) {
    return 0;
  }

  let latestYear = 0;
  let latestYearDeals = 0;
  let totalDeals = Number(stats?.deals_total) || 0;

  years.forEach((entry) => {
    const year = Number(entry?.y) || 0;
    const yearDeals = Number(entry?.all) || 0;

    if (year > latestYear) {
      latestYear = year;
      latestYearDeals = yearDeals;
    } else if (year === latestYear) {
      latestYearDeals += yearDeals;
    }
  });

  if (totalDeals <= 0) {
    totalDeals = years.reduce((sum, entry) => sum + (Number(entry?.all) || 0), 0);
  }

  return latestYear * 1000000 + latestYearDeals * 1000 + totalDeals;
}

function sortOptions(optionsMap) {
  return [...optionsMap.values()].sort((left, right) =>
    left.label.localeCompare(right.label)
  );
}

function matchesSelection(selectedValues, investorValues) {
  if (!selectedValues.length) {
    return true;
  }

  const investorKeys = new Set(
    investorValues
      .map(normalizeKey)
      .filter(Boolean)
  );

  return selectedValues.some((selectedValue) => investorKeys.has(normalizeKey(selectedValue)));
}

export function buildBoardViewOptions(columns = []) {
  const countries = new Map();
  const types = new Map();
  const industries = new Map();
  let totalCount = 0;

  (columns || []).forEach((column) => {
    (column?.investors || []).forEach((investor) => {
      totalCount += 1;

      const country = getInvestorCountry(investor);
      if (country) {
        const key = normalizeKey(country);
        countries.set(key, {
          value: country,
          label: country,
          count: (countries.get(key)?.count || 0) + 1,
        });
      }

      getInvestorTypes(getInvestorDetails(investor)).forEach((type) => {
        const normalizedType = normalizeValue(type);
        if (!normalizedType) {
          return;
        }

        const key = normalizeKey(normalizedType);
        types.set(key, {
          value: normalizedType,
          label: normalizedType,
          count: (types.get(key)?.count || 0) + 1,
        });
      });

      getInvestorIndustries(getInvestorDetails(investor)).forEach((industry) => {
        const normalizedIndustry = normalizeValue(industry);
        if (!normalizedIndustry) {
          return;
        }

        const key = normalizeKey(normalizedIndustry);
        industries.set(key, {
          value: normalizedIndustry,
          label: normalizedIndustry,
          count: (industries.get(key)?.count || 0) + 1,
        });
      });
    });
  });

  return {
    totalCount,
    countries: sortOptions(countries),
    types: sortOptions(types),
    industries: sortOptions(industries),
  };
}

export function sanitizeBoardViewFilters(filters, options) {
  const availableCountries = new Set((options?.countries || []).map((option) => normalizeKey(option.value)));
  const availableTypes = new Set((options?.types || []).map((option) => normalizeKey(option.value)));
  const availableIndustries = new Set((options?.industries || []).map((option) => normalizeKey(option.value)));

  return {
    countries: (filters?.countries || []).filter((value) => availableCountries.has(normalizeKey(value))),
    types: (filters?.types || []).filter((value) => availableTypes.has(normalizeKey(value))),
    industries: (filters?.industries || []).filter((value) => availableIndustries.has(normalizeKey(value))),
  };
}

export function hasBoardViewFilters(filters = {}) {
  return Boolean(
    (filters?.countries || []).length ||
      (filters?.types || []).length ||
      (filters?.industries || []).length
  );
}

function compareInvestors(leftInvestor, rightInvestor, sortMode, investorStatsMap) {
  if (sortMode === 'valueDesc') {
    const diff = getInvestorValue(rightInvestor) - getInvestorValue(leftInvestor);
    if (diff !== 0) {
      return diff;
    }
  }

  if (sortMode === 'valueAsc') {
    const diff = getInvestorValue(leftInvestor) - getInvestorValue(rightInvestor);
    if (diff !== 0) {
      return diff;
    }
  }

  if (sortMode === 'recentActivity') {
    const diff = getRecentActivityScore(rightInvestor, investorStatsMap)
      - getRecentActivityScore(leftInvestor, investorStatsMap);

    if (diff !== 0) {
      return diff;
    }
  }

  return getInvestorDisplayName(leftInvestor).localeCompare(getInvestorDisplayName(rightInvestor));
}

export function applyBoardView(columns = [], filters = {}, sortMode = 'manual', investorStatsMap = {}) {
  const selectedCountries = filters?.countries || [];
  const selectedTypes = filters?.types || [];
  const selectedIndustries = filters?.industries || [];
  let visibleCount = 0;

  const nextColumns = (columns || []).map((column) => {
    const nextInvestors = (column?.investors || [])
      .filter((investor) => {
        const details = getInvestorDetails(investor);
        const countryMatches = matchesSelection(selectedCountries, [details?.country]);
        const typeMatches = matchesSelection(selectedTypes, getInvestorTypes(details));
        const industryMatches = matchesSelection(selectedIndustries, getInvestorIndustries(details));

        return countryMatches && typeMatches && industryMatches;
      });

    if (sortMode !== 'manual') {
      nextInvestors.sort((leftInvestor, rightInvestor) =>
        compareInvestors(leftInvestor, rightInvestor, sortMode, investorStatsMap)
      );
    }

    visibleCount += nextInvestors.length;

    return {
      ...column,
      investors: nextInvestors,
    };
  });

  return {
    columns: nextColumns,
    visibleCount,
  };
}
